import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NavController } from '@ionic/angular';
import { gql } from 'apollo-angular';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  POSTJOB_ORIGIN_QUERY_PARAM,
  POSTJOB_SOURCE_QUERY_PARAM,
  SCHEDULED_EVENT_DERIVED_UNIQUE_ID_PARAM,
  SCHEDULED_EVENT_OCCURRENCE_ID_PARAM,
  navigationValueMappingJobObj,
  postJobsServiceNavigation,
  postJobsServiceNavigationSubMenu,
} from 'src/app/pages/post-jobs/constants/post-job.constants';
import { NavigationTracker } from 'src/app/pages/post-jobs/models/navigation-tracker.model';
import { PostJobQueryParams } from 'src/app/pages/post-jobs/models/post-job-query-params.model';
import { ConsumerAccount } from 'src/app/shared/models/gql/consumer-account.model';
import { ConvertJobToPublicRequest } from 'src/app/shared/models/gql/convert-job-to-public-request.model';
import { JobCreateUpdateRequest } from 'src/app/shared/models/gql/job-create-update-request.model';
import { JobDetail } from 'src/app/shared/models/gql/job-detail.model';
import { JobPostNavigation } from 'src/app/shared/models/gql/job-post-navigation';
import { JobPostSubNavigation } from 'src/app/shared/models/gql/job-post-sub-navigation';
import { JobSessionWeekDays } from 'src/app/shared/models/gql/job-sessions-week-days.model';
import { JobSessions } from 'src/app/shared/models/gql/job-sessions.model';
import { JobStaticQuestions } from 'src/app/shared/models/gql/job-static-questions.model';
import { UserDraft } from 'src/app/shared/models/gql/user-draft.model';
import { JobServiceCategory } from 'src/app/shared/models/job-service-category.model';
import { JobServiceListItem } from 'src/app/shared/models/job-service-list-item.model';
import { PostedJobRequest } from 'src/app/shared/models/posted-job-request.model';
import { ApiInterfaceService } from 'src/app/shared/services/api-interface.service';
import { PromptsService } from 'src/app/shared/services/prompts.service';
import { UserDraftService } from 'src/app/shared/services/user-draft.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { draftJobKind } from 'src/app/shared/types/draft-job-kind';
import { GenderPreference } from 'src/app/shared/types/gender-preference';
import { JobType } from 'src/app/shared/types/job-type';
import { PrivateEditRequestType } from 'src/app/shared/types/private-job-edit-options';
import { getGraphQLErrorText } from 'src/app/shared/utilities/graphql-error-handling';

@Injectable({
  providedIn: 'root',
})
export class PostJobsService implements OnDestroy {
  // TODO: ES-15056 create an interface for describing a particular day of the week same in web.
  // Expected weekdays array
  public weekDaysOptions: readonly {
    shortDay: string;
    day: string;
    name: string;
  }[] = Object.freeze([
    {
      shortDay: 'Mon',
      day: 'Monday',
      name: 'monday',
    },
    {
      shortDay: 'Tue',
      day: 'Tuesday',
      name: 'tuesday',
    },
    {
      shortDay: 'Wed',
      day: 'Wednesday',
      name: 'wednesday',
    },
    {
      shortDay: 'Thu',
      day: 'Thursday',
      name: 'thursday',
    },
    {
      shortDay: 'Fri',
      day: 'Friday',
      name: 'friday',
    },
    {
      shortDay: 'Sat',
      day: 'Saturday',
      name: 'saturday',
    },
    {
      shortDay: 'Sun',
      day: 'Sunday',
      name: 'sunday',
    },
  ]);

  // This variable is used to pass post jobs object
  private postJobsObjectEvent: BehaviorSubject<JobDetail> = new BehaviorSubject<JobDetail>(null);

  // Selected unsupported services for a draft job
  private selectedUnsupportedServices: BehaviorSubject<string[]> = new BehaviorSubject<string[]>(null);

  // This variable is used to show message after draft updated
  private isProgressSaveMsgShown: Subject<boolean> = new Subject();

  // Total steps of job posting form
  private totalSteps: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  // indicates the current step number
  private currentStep: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  // Error handling params formControlName and errorMessage
  private errorHandlingParams: {
    formControlName?: string[];
    errorMessage?: string[];
  } = {
    formControlName: [],
    errorMessage: [],
  };

  // daily living services
  dailyLivingSocialCommunityCategories: JobServiceListItem[] = [];

  // personal care services
  personalCareServiceCategories: JobServiceListItem[] = [];

  // Allied health service
  alliedHealthserviceCategories: JobServiceListItem[] = [];

  // Used to show modal like "Auto-save not saving" or "Exiting auto-saved job"
  private isAutoSaveFail = false;

  // job type is public or private from query param
  jobType: JobType;

  // worker id to get the one to one channel
  workerId: string;

  // mode used to update public or convert private job to public.
  mode: string;

  // Used to indicate if private job is being edited or converted to public job
  privateEditRequestType: PrivateEditRequestType;

  // navigation main steps
  sideNav: JobPostNavigation;

  // navigation sub steps
  navigationSubMenu: JobPostSubNavigation[];

  // Store draft job kind
  private draftJobKind: draftJobKind;

  private onDestroySubject = new Subject<void>();

  constructor(
    private api: ApiInterfaceService,
    private navController: NavController,
    private userDraftService: UserDraftService,
    private utilityService: UtilityService,
    private promptsService: PromptsService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {}

  /**
   * Getter to get draft job kind.
   */
  get getDraftJobKind(): draftJobKind {
    return this.draftJobKind;
  }

  /**
   * Setter to set draft job kind.
   *
   * @param draftJobKind - draft job kind
   */
  set setDraftJobKind(draftJobKind: draftJobKind) {
    this.draftJobKind = draftJobKind;
  }

  /**
   * Update post jobs object using behavior subject.
   *
   * @param postJobObject - Post jobs object data.
   */
  updatePostJobsObject(postJobObject: JobDetail) {
    if (postJobObject) {
      postJobObject = {
        ...postJobObject,
        ...this.getTrackingFromQueryParams(postJobObject),
      };
    }

    this.postJobsObjectEvent.next(postJobObject);

    // for draft job the id won't exist as it lives in a different table.
    if (postJobObject && !postJobObject?.id) {
      this.saveDraft(postJobObject);
    }
  }

  /**
   * Fetch post jobs object.
   */
  getPostJobsObject(): Observable<JobDetail> {
    return this.postJobsObjectEvent.asObservable();
  }

  /**
   * Fetch selected unsupported services
   */
  getSelectedUnsupportedServices(): Observable<string[]> {
    return this.selectedUnsupportedServices.asObservable();
  }

  /**
   * Fetch progress save message value.
   */
  getIsProgressSaveMsgShownValue(): Observable<boolean> {
    return this.isProgressSaveMsgShown.asObservable();
  }

  /**
   * Fetch current step value.
   */
  getCurrentStepValue(): Observable<number> {
    return this.currentStep.asObservable();
  }

  /**
   * Update current step value.
   *
   * @param value - updated current step value.
   */
  updateCurrentStepValue(value) {
    this.currentStep.next(value);
  }

  /**
   * Fetch total step value.
   */
  getTotalStepsValue(): Observable<number> {
    return this.totalSteps.asObservable();
  }

  /**
   * Update total steps value.
   *
   * @param value - updated total step value.
   */
  updateTotalStepsValue(value) {
    this.totalSteps.next(value);
  }

  /**
   * Getter to get the auto save value if draft not saved.
   */
  get isAutoSaveFailValue(): boolean {
    return this.isAutoSaveFail;
  }

  /**
   * Getter to get the error handling params.
   *
   * @readonly
   * @type {string[]}
   * @memberof PostJobsService
   */
  get getErrorHandlingParams(): {
    formControlName?: string[];
    errorMessage?: string[];
  } {
    return this.errorHandlingParams;
  }

  /**
   * Setter to update error handling params for each step.
   *
   * @param params - error message & form control
   * @memberof PostJobsService
   */
  set setErrorHandlingParams(params: { formControlName?: string[]; errorMessage?: string[] }) {
    this.errorHandlingParams = params;
  }

  /**
   * Sends a request to get the service categories.
   *
   * @param {*} category - This should be either daily_living or personal_care only
   */
  async getServiceCategories(category: JobServiceCategory): Promise<JobServiceListItem[]> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query clientPostedJobsServiceCategories($category: PostedJobsServiceCategories!) {
            clientPostedJobsServiceCategories(category: $category) {
              id
              title
              subTitle
              isImportant
              details
              unsupported
            }
          }
        `,
        {
          category,
        },
      );
      return res['clientPostedJobsServiceCategories'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Sends a request to fetch job details.
   *
   * @param id - Id for get data for particular job.
   */
  async getPostedJobDetailsById(id: string | number): Promise<JobDetail> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query postedJob($id: ID!) {
            postedJob(id: $id) {
              id
              title
              frequency
              consumerDescription
              carerDescription
              jobDescription
              scheduling
              estimatedHoursPerWeek
              supportSessionsPerWeek
              estimatedHoursPerPeriod
              supportSessionsPerPeriod
              sessionFrequency
              timingDescription
              participant {
                id
              }
              timestamps {
                startTime
              }
              jobSessions {
                id
                weekDay
                period {
                  endTime
                  startTime
                }
              }
              supportDetails {
                name
                details
              }
              serviceCategories {
                name
                code
              }
              location {
                address
                id
              }
              preferredWorker {
                gender
              }
            }
          }
        `,
        {
          id,
        },
      );
      return res['postedJob'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Sends a GET request for fetching participant details.
   */
  async getParticipantDetails(): Promise<ConsumerAccount['profile']> {
    try {
      const res = await this.api.gqlQuery(gql`
        {
          user {
            profile {
              ... on ClientProfile {
                id
                participantRelationship
                participantAttributes {
                  id
                  firstName
                  gender
                  displayGender
                  age
                }
              }
            }
          }
        }
      `);
      return res['user']['profile'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Sends a request to fetch job details.
   *
   * @param page - page name.
   */
  async getClientPostedJobStaticQuestions(page: string): Promise<JobStaticQuestions[]> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query clientPostedJobStaticQuestions($page: PostedJobsPage!) {
            clientPostedJobStaticQuestions(page: $page) {
              name
              options {
                label
                value
              }
            }
          }
        `,
        {
          page,
        },
      );
      return res['clientPostedJobStaticQuestions'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Creating and updating regular or private job.
   *
   * @param postedJobCreateUpdateInput - job create or update object.
   */
  async createUpdateRegularOrPrivateJob(postedJobCreateUpdateInput: JobCreateUpdateRequest): Promise<JobDetail> {
    try {
      const postedJobData = await this.api.gqlMutation(
        gql`
          mutation postedJobCreateUpdate($postedJobCreateUpdateInput: PostedJobCreateUpdateInput!) {
            postedJobCreateUpdate(input: $postedJobCreateUpdateInput) {
              postedJob {
                clientFields {
                  postedWithin14DaysOfUserSignUp
                  firstPostedJob
                }
                id
                supportDetails {
                  name
                  details
                }
              }
            }
          }
        `,
        { postedJobCreateUpdateInput },
      );
      return postedJobData['postedJobCreateUpdate']['postedJob'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Sends a mutation for converting a private job to public job.
   *
   * @param privateJobObject - private job object.
   */
  async convertPrivateJobToPublic(privateJobObject: ConvertJobToPublicRequest): Promise<JobDetail> {
    try {
      const res = await this.api.gqlMutation(
        gql`
          mutation convertJobToPublic($convertJobToPublicInput: ConvertJobToPublicInput!) {
            convertJobToPublic(input: $convertJobToPublicInput) {
              postedJob {
                id
              }
            }
          }
        `,
        { convertJobToPublicInput: privateJobObject },
      );
      return res['convertJobToPublic']['postedJob'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Set request object.
   *
   * @param postJobObject - post job details object.
   * @param jobType - private or public job posting.
   */
  setRequestObj(postJobObject: JobDetail, jobType: string): PostedJobRequest {
    const supportActivities: string[] = [];

    if (postJobObject?.supportActivities?.basic_nurse) {
      supportActivities.push('basic_nurse');
    }

    // If basic_social is not selected, we should deselect all dailyLivingSocialCommunityServices
    const dailyLivingSocialCommunityServices =
      postJobObject?.supportActivities?.basic_social && postJobObject?.dailyLivingSocialCommunityServices
        ? postJobObject?.dailyLivingSocialCommunityServices
        : [];
    // If basic_personal is not selected, we should deselect all personalCareServices
    const personalCareServices =
      postJobObject?.supportActivities?.basic_personal && postJobObject?.personalCareServices
        ? postJobObject?.personalCareServices
        : [];

    // If basic_allied is not selected, we should deselect all alliedHealthServices
    const alliedHealthServices =
      postJobObject?.supportActivities?.basic_allied && postJobObject?.alliedHealthServices
        ? [].concat(postJobObject?.alliedHealthServices)
        : [];

    // applies the query param tracking to the job creation request object
    const { source, origin, scheduledEventDerivedUniqueId, scheduledEventOccurrenceId } =
      this.getTrackingFromQueryParams(postJobObject);
    const requestBody: PostedJobRequest = {
      title: postJobObject?.title,
      jobDescription: postJobObject?.jobDescription || '',
      consumerDescription: postJobObject?.consumerDescription,
      carerDescription: postJobObject?.carerDescription,
      frequency: postJobObject?.frequency || (postJobObject?.sessionFrequency === 'once_off' ? 'once_off' : 'ongoing'),
      sessionFrequency: postJobObject?.sessionFrequency,
      scheduling: postJobObject?.scheduling,
      participantId: postJobObject?.participant?.id,
      kind: jobType === 'public' ? 'public' : 'private',
      searchFilter: {
        basicSearch: [
          ...supportActivities,
          ...dailyLivingSocialCommunityServices,
          ...personalCareServices,
          ...alliedHealthServices,
        ],
        advanced: {
          gender: postJobObject?.preferredWorker?.gender?.toLowerCase() as GenderPreference,
        },
        suburb: {
          idx: postJobObject?.location?.postCodeGeoId,
          txt: postJobObject?.location?.address,
        },
      },
      supportSessionsPerWeek: postJobObject?.supportSessionsPerWeek || 0,
      estimatedHoursPerWeek: postJobObject?.estimatedHoursPerWeek || 0,
      supportSessionsPerPeriod: postJobObject?.supportSessionsPerPeriod || 0,
      estimatedHoursPerPeriod: postJobObject?.estimatedHoursPerPeriod || 0,
      startDate: postJobObject?.supportStartDate || null,
      timingDescription: postJobObject?.timingDescription,
      jobSessions: postJobObject?.jobSessions ? this.generateJobSessionsArrayObj(postJobObject) : null,
      houseworkKind: postJobObject?.houseworkKind,
      houseworkEquipment: postJobObject?.houseworkEquipment,
      supportKind: postJobObject?.supportKind,
      supportLocation: postJobObject?.supportLocation,
      transportKind: postJobObject?.transportKind,
      transportLocation: postJobObject?.transportLocation,
      vehicleRequirement: postJobObject?.vehicleRequirement,
      vehicleRequirementDetails: postJobObject?.vehicleRequirementDetails,

      // properties below are mainly used for tracking
      source,
      origin,
      scheduledEventDerivedUniqueId,
      scheduledEventOccurrenceId,
    };

    // if gender is not selected then no need to send the gender field
    if (!requestBody?.searchFilter?.advanced?.gender) {
      delete requestBody.searchFilter.advanced.gender;
    }

    return requestBody;
  }

  /**
   * Generate array of objects for job sessions.
   *
   * @param postJobObject - post job details object.
   */
  private generateJobSessionsArrayObj(postJobObject: JobDetail): JobSessions[] {
    const dayOfWeek = [];
    this.weekDaysOptions.forEach((weekDayObj) => {
      const weekDaySlots = postJobObject?.jobSessions[weekDayObj.name];
      if (weekDaySlots?.weekDaySelected) {
        const keyData = weekDaySlots?.timeSlots.map((slot) => ({
          id: '',
          endTime: this.utilityService.convertIonicDatetimeTo24Hours(slot.endTime),
          startTime: this.utilityService.convertIonicDatetimeTo24Hours(slot.startTime),
          weekDay: weekDayObj.day,
        }));
        if (keyData?.length > 0) {
          dayOfWeek.push(...keyData);
        }
      }
    });
    return dayOfWeek;
  }

  /**
   * Update/create draft whenever user update post job details.
   *
   * @param postJobObject - post job details object.
   */
  async saveDraft(postJobObject: JobDetail): Promise<void> {
    const supportActivities = {
      basicSocial: !!postJobObject?.supportActivities?.basic_social,
      basicPersonal: !!postJobObject?.supportActivities?.basic_personal,
    };

    const supportActivitiesIncludingAlliedHealth = {
      ...supportActivities,
      basicAllied: !!postJobObject?.supportActivities?.basic_allied,
    };

    // It adds navigationTracker property when saving the job draft.
    const navigationTracker = this.createNavigationTrackerObject(this.sideNav.navigation);
    if (!postJobObject?.draftId) {
      this.selectedUnsupportedServices.next([]);
    }

    const reqObject: { action: 'create' | 'update'; attributes: UserDraft } = {
      action: postJobObject?.draftId ? 'update' : 'create',
      attributes: {
        id: postJobObject?.draftId ? Number(postJobObject.draftId) : null,
        kind: this.getDraftJobKind,
        content: JSON.stringify({
          jobObj: {
            ...supportActivitiesIncludingAlliedHealth,
            ...this.setRequestObj(postJobObject, this.jobType),
          },
          navigationTracker,
          selectedUnsupportedServices: postJobObject?.draftId ? this.selectedUnsupportedServices.value : [],
        }),
      },
    };

    this.updateSideNavigationObject(this.sideNav.navigation, JSON.parse(reqObject.attributes.content));

    try {
      const userDraft: UserDraft = await this.userDraftService.createUpdateDeleteUserDraft(reqObject);

      if (userDraft.id && !!postJobObject) {
        this.isAutoSaveFail = false;

        postJobObject.draftId = userDraft.id;

        this.isProgressSaveMsgShown.next(true);
      }
    } catch (error) {
      this.isAutoSaveFail = true;

      this.utilityService.showErrorAlert(getGraphQLErrorText(error));
    }
  }

  /**
   * Generate job object and update postJobsObjectEvent behavior subject.
   *
   * @param draftDetails - drafted job object.
   */
  async loadDraft(draftDetails: UserDraft): Promise<void> {
    if (this.dailyLivingSocialCommunityCategories.length === 0) {
      this.dailyLivingSocialCommunityCategories = await this.getServiceCategories('daily_living');
    }

    if (this.personalCareServiceCategories.length === 0) {
      this.personalCareServiceCategories = await this.getServiceCategories('personal_care');
    }

    if (this.alliedHealthserviceCategories.length === 0) {
      this.alliedHealthserviceCategories = await this.getServiceCategories('allied_health');
    }

    const content = JSON.parse(draftDetails.content);

    const jobObject: JobDetail = {
      draftId: draftDetails.id,
      location: {
        postCodeGeoId: content.jobObj.searchFilter.suburb.idx,
        address: content.jobObj.searchFilter.suburb.txt,
      },
      supportActivities: {
        basic_allied: content.jobObj.basicAllied || !!content.jobObj.searchFilter.basicSearch.includes('basic_allied'),
        basic_nurse: !!content.jobObj.searchFilter.basicSearch.includes('basic_nurse'),
        basic_social: content.jobObj.basicSocial,
        basic_personal: content.jobObj.basicPersonal,
      },
      personalCareServices: this.getSelectedServices(
        this.personalCareServiceCategories,
        content.jobObj.searchFilter?.basicSearch,
      ),
      dailyLivingSocialCommunityServices: this.getSelectedServices(
        this.dailyLivingSocialCommunityCategories,
        content.jobObj.searchFilter?.basicSearch,
      ),
      alliedHealthServices: this.getSelectedServices(
        this.alliedHealthserviceCategories,
        content.jobObj.searchFilter?.basicSearch,
      )[0],
      frequency: content.jobObj.frequency,
      supportSessionsPerWeek: content.jobObj.supportSessionsPerWeek,
      estimatedHoursPerWeek: content.jobObj.estimatedHoursPerWeek,
      sessionFrequency: content.jobObj?.sessionFrequency || null,
      supportSessionsPerPeriod: content.jobObj?.supportSessionsPerPeriod || 0,
      estimatedHoursPerPeriod: content.jobObj?.estimatedHoursPerPeriod || 0,
      scheduling: content.jobObj.scheduling,
      supportStart: content.jobObj.startDate ? 'specificDate' : 'asap',
      supportStartDate: content.jobObj.startDate,
      jobSessions: content.jobObj.jobSessions
        ? (this.jobSessionWeekDays(content.jobObj.jobSessions) as JobSessions[])
        : [],
      timingDescription: content.jobObj.timingDescription,
      title: content.jobObj.title,
      jobDescription: content.jobObj.jobDescription,
      consumerDescription: content.jobObj.consumerDescription,
      houseworkKind: content.jobObj.houseworkKind,
      houseworkEquipment: content.jobObj.houseworkEquipment,
      supportKind: content.jobObj.supportKind,
      supportLocation: content.jobObj.supportLocation,
      transportKind: content.jobObj.transportKind,
      transportLocation: content.jobObj.transportLocation,
      vehicleRequirement: content.jobObj.vehicleRequirement,
      vehicleRequirementDetails: content.jobObj.vehicleRequirementDetails,
    };

    // check if the source (where the job creation is initiated) is available
    // mainly used for tracking
    if (content.jobObj?.source) {
      jobObject.source = content.jobObj?.source;
    }

    // check if the origin (which event triggered the creation of job) is available
    // mainly used for tracking
    if (content.jobObj?.origin) {
      jobObject.origin = content.jobObj?.origin;
    }

    // check if the scheduledEventDerivedUniqueId is available (if this is a replacement job for cancelled session)
    if (content.jobObj?.scheduledEventDerivedUniqueId) {
      jobObject.scheduledEventDerivedUniqueId = content.jobObj?.scheduledEventDerivedUniqueId;
    }

    if (this.jobType === 'public') {
      jobObject.carerDescription = content.jobObj.carerDescription;
      jobObject.preferredWorker = {
        gender: (content?.jobObj?.searchFilter?.advanced?.gender
          ? this.utilityService.capitalize(content?.jobObj?.searchFilter?.advanced?.gender)
          : 'nopref') as GenderPreference,
      };
    }

    this.selectedUnsupportedServices.next(content?.selectedUnsupportedServices);

    // If draft content has navigationTracker property, Then using navigationTracker to update side navigation menu
    // Otherwise, if some older drafts don't have navigationTracker property, it uses navigationObj to update side navigation menu
    if (content.navigationTracker) {
      this.updateSideNavigationObject(this.sideNav.navigation, content);
    } else if (Object.keys(content.navigationObj).length) {
      this.modifyAndUpdateSideNavObject(content.navigationObj);

      this.updateSideNavIsCompletedValue();
    }

    this.updatePostJobsObject(jobObject);
  }

  /**
   * Sync local navigation object with draft navigation object.
   * Modify and update side nav object and sub navigation object.
   * Update sideNav and navigationSubMenu without removing references.
   *
   * @param navigationObj - draft navigation object.
   */
  private modifyAndUpdateSideNavObject(navigationObj: JobPostNavigation['navigation']): void {
    navigationObj.forEach((navigation) => {
      navigation.subNavigation.forEach((subNavigation) => {
        if (subNavigation.isCompleted) {
          this.updateCurrentStepValue(this.currentStep.value + 1);
        }

        this.updateSubMenuPreferences(subNavigation, this.jobType);

        // update object without changing reference
        Object.assign(
          this.navigationSubMenu[this.navigationSubMenu.findIndex((subNav) => subNav.value === subNavigation.value)],
          subNavigation,
        );
      });

      const index = this.sideNav.navigation.findIndex((sideNav) => sideNav.value === navigation.value);

      // update side nav object value
      if (index > -1) {
        this.sideNav.navigation[index].isCollapsed = navigation.isCollapsed;
        this.sideNav.navigation[index].isCompleted = navigation.isCompleted;
      }
    });

    this.countTotalSteps();
  }

  /**
   * Return job session object to set value into form.
   *
   * @param jobSessions - Job sessions array
   */
  jobSessionWeekDays(jobSessions: JobSessions[]): JobSessionWeekDays {
    const weekDays: JobSessionWeekDays = {
      monday: { weekDaySelected: null, timeSlots: [] },
      tuesday: { weekDaySelected: null, timeSlots: [] },
      wednesday: { weekDaySelected: null, timeSlots: [] },
      thursday: { weekDaySelected: null, timeSlots: [] },
      friday: { weekDaySelected: null, timeSlots: [] },
      saturday: { weekDaySelected: null, timeSlots: [] },
      sunday: { weekDaySelected: null, timeSlots: [] },
    };

    // weekday wise groupby the job session time slots
    const selectedJobSessions: JobSessions = jobSessions?.reduce((groupedJobSessions, weekDayObj) => {
      groupedJobSessions[weekDayObj.weekDay] = [...(groupedJobSessions[weekDayObj.weekDay] || []), weekDayObj];
      return groupedJobSessions;
    }, {});

    // Create the time slots based on the available hours for the weekdays.
    Object.keys(selectedJobSessions).forEach((availabilityItem) => {
      weekDays[availabilityItem?.toLowerCase()].weekDaySelected = true;

      // creating the timeslots as many as the available hours
      selectedJobSessions[availabilityItem]?.forEach((dayItem) => {
        weekDays[availabilityItem?.toLowerCase()].timeSlots.push({
          startTime: this.utilityService.convert24HrsToIonicDatetime(dayItem.startTime),
          endTime: this.utilityService.convert24HrsToIonicDatetime(dayItem.endTime),
        });
      });
    });

    return weekDays;
  }

  /**
   * Return selected service categories.
   *
   * @param serviceCategories - Service categories.
   * @param basicSearch - Selected service category values.
   */
  private getSelectedServices(serviceCategories: JobServiceListItem[], basicSearch: string[]): string[] {
    return serviceCategories
      .map((serviceCategory) => serviceCategory.id)
      .filter((serviceCategoryId) => basicSearch.includes(serviceCategoryId));
  }

  /**
   * Finds the previous page by looping through the navigation sub menu steps from the current page until the one the user has access to.
   *
   * @param currentStepValue
   */
  redirectToPreviousStep(currentStepValue: string): void {
    // Stores current page index.
    const currentMenuIndex = this.navigationSubMenu.findIndex((subNav) => currentStepValue === subNav.value);

    if (currentMenuIndex < this.navigationSubMenu.length) {
      // find previous step index
      for (let index = currentMenuIndex - 1; index >= 0; index--) {
        // check if user can see this page
        if (this.navigationSubMenu[index].isShown) {
          this.updateCurrentStepValue(this.currentStep.value - 1);

          // Navigates to the found link.
          this.navController.navigateBack([`/post-jobs/add-job/${this.jobType}${this.navigationSubMenu[index].link}`]);

          break;
        }
      }
    }
  }

  /**
   * Finds the next page by looping through the navigation sub menu steps
   * from the current page until the one the user has access to.
   *
   * @param currentStepValue - current step value string
   */
  async redirectToNextStep(currentStepValue: string): Promise<void> {
    // Stores current page index.
    const currentMenuIndex = this.navigationSubMenu.findIndex((subNav) => currentStepValue === subNav.value);

    if (currentMenuIndex < this.navigationSubMenu.length) {
      // find next step index
      for (let index = currentMenuIndex + 1; index < this.navigationSubMenu.length; index++) {
        // check if user can see this page
        if (this.navigationSubMenu[index].isShown) {
          this.updateCurrentStepValue(this.currentStep.value + 1);

          this.countTotalSteps();
          this.navController.navigateForward(
            [`/post-jobs/add-job/${this.jobType}${this.navigationSubMenu[index].link}`],
            {
              queryParamsHandling: 'preserve',
            },
          );

          this.navigationSubMenu[currentMenuIndex].isCompleted = true;

          this.updateSideNavIsCompletedValue();

          this.saveDraft(this.postJobsObjectEvent.value);

          break;
        }
      }
    }
  }

  /**
   * Update subNav isShown value based params.
   *
   * @param subNavValue - Array of strings indicating Sub navigation value/s to update.
   * @param isShown - Boolean value for show hide sub navigation.
   */
  hideShowSubNavItems(subNavValue: string[], isShown: boolean): void {
    const subNavigation = this.navigationSubMenu.filter((subNav) => subNavValue.includes(subNav.value));

    subNavigation.forEach((subNav) => {
      subNav.isShown = isShown;
      if (!isShown) {
        subNav['isCompleted'] = isShown;
      }
    });
  }

  /**
   * Update side nav parent value if all child steps are completed.
   */
  updateSideNavIsCompletedValue() {
    // changed icon of navigation menu to checked if completed
    this.sideNav.navigation.forEach((nav) => {
      const subMenu = this.navigationSubMenu.filter((subNav) => nav.value === subNav.parentNavValue && subNav.isShown);

      // count on how many sub menus are completed
      nav.isCompleted = subMenu.every((subNav) => subNav.isCompleted);
    });
  }

  /**
   * Check how many steps are visibale to user.
   */
  countTotalSteps() {
    this.updateTotalStepsValue(this.navigationSubMenu.filter((subNav) => subNav.isShown).length);
  }

  /**
   * Get draft job details.
   *
   * @param jobType - private or public job posting.
   * @param workerId - redirect back to worker profile page.
   * @param postJobQueryParams - the query parameters that will be passed along to the job creation.
   */
  async getDraftJobDetails(
    jobType: JobType,
    workerId?: string,
    postJobQueryParams: PostJobQueryParams = {},
  ): Promise<void> {
    this.jobType = jobType;

    this.workerId = workerId;

    let draftDetails: UserDraft[] = [];

    this.setDraftJobKind = this.jobType === 'private' ? 'postedJobPrivate' : 'postedJobPublic';

    try {
      if (this.jobType === 'public') {
        draftDetails = await this.userDraftService.retrieveDrafts('postedJobPublic');
      } else {
        draftDetails.push(
          await this.userDraftService.retrieveDraft(this.jobType === 'private' ? 'postedJobPrivate' : 'postedJob'),
        );
      }

      if (draftDetails.length && draftDetails[0].id) {
        this.openResumeDialog(draftDetails, postJobQueryParams);
      } else {
        this.initialiseSideNavigation(this.jobType);
        this.router.navigate([`/post-jobs/add-job/${this.jobType}/next-incomplete-step`], {
          queryParams: postJobQueryParams,
          queryParamsHandling: 'merge',
        });
      }
    } catch (error) {
      this.utilityService.showErrorAlert(getGraphQLErrorText(error));
    }
  }

  /**
   * Set content of resume dialog based on optimizely flag and number of drafts.
   *
   * @param draftsLength - number of drafts
   * @returns - content string to be used in resume dialog
   */
  getResumeDialogContent(draftsLength: number): string {
    if (this.jobType === 'public') {
      if (draftsLength > 1) {
        return `You have ${draftsLength} saved job drafts. You can resume a draft or start a new job.`;
      }
      return 'You have 1 saved job draft. You can resume your draft or start a new job.';
    }
    return 'We’ve saved your job in progress. You can resume this job or delete it and start a new job.';
  }

  /**
   * Find next incomplete step and redirect to that step.
   *
   * @param draftDetails - drafted job object.
   * @param postJobQueryParams - the query parameters that will be passed along to the job creation.
   */
  openResumeDialog(draftDetails: UserDraft[], postJobQueryParams: PostJobQueryParams = {}): void {
    const content = this.getResumeDialogContent(draftDetails.length);

    this.promptsService.showConfirmAlert({
      header: 'Resume your saved job?',
      message: content,
      buttons: [
        {
          text: 'Resume',
          handler: async () => {
            if (draftDetails.length === 1) {
              this.router.navigate([`/post-jobs/add-job/${this.jobType}/next-incomplete-step`], {
                queryParams: {
                  draftId: draftDetails[0].id,
                  ...postJobQueryParams,
                },
                queryParamsHandling: 'merge',
              });
            } else {
              this.router.navigate(['tabs/jobs/consumer'], {
                queryParams: postJobQueryParams,
              });
            }
          },
        },
        {
          text: 'Start',
          handler: async () => {
            await this.deleteDraft();
            this.initialiseSideNavigation(this.jobType);
            this.router.navigate([`/post-jobs/add-job/${this.jobType}/next-incomplete-step`], {
              queryParams: postJobQueryParams,
              queryParamsHandling: 'merge',
            });
          },
        },
      ],
      backdropDismiss: false,
    });
  }

  /**
   * Delete posted job draft.
   *
   * @param draftDetails
   */
  private async deleteDraft(): Promise<void> {
    try {
      await this.userDraftService.createUpdateDeleteUserDraft({
        action: 'delete',
        attributes: {
          kind: this.jobType === 'private' ? 'postedJobPrivate' : 'postedJob',
        },
      });
    } catch (error) {
      this.utilityService.showErrorAlert(getGraphQLErrorText(error));
    }
  }

  /**
   * Update the side navigation menu of the current draft based on navigationTracker and jobObj from draft content.
   *
   * @param navigation - The side navigation menu.
   * @param draftContent - the current draft content.
   * @param draftContent.navigationTracker - navigationTracker object in the draft content.
   * @param draftContent.jobObj - jobObj object in the draft content.
   */
  private updateSideNavigationObject(
    navigation: JobPostNavigation['navigation'],
    { navigationTracker, jobObj },
  ): JobPostNavigation['navigation'] {
    let isMatchedSubNavigationFound = false;
    // Loop through side navigation to update isShown, isCollapsed and isComplete for the menus and sub menus based on navigationTracker and jobObj
    navigation.forEach((menu) => {
      menu.subNavigation.forEach((subMenu) => {
        if (subMenu.value === navigationTracker.subNavigationValue) {
          isMatchedSubNavigationFound = true;
        }

        this.updateSubMenuPreferences(subMenu, jobObj.kind);
        this.updateSubMenuByJobObj(subMenu, jobObj, isMatchedSubNavigationFound);
      });

      menu.isCollapsed =
        !isMatchedSubNavigationFound || menu.value === navigationTracker.navigationValue || menu.isCollapsed;
      menu.isCompleted = !isMatchedSubNavigationFound;
    });
    return navigation;
  }

  /**
   * Update preferences isShown value if draft is public or private.
   *
   * @param subMenu - the side navigation sub menu.
   * @param jobKind - the draft job kind.
   */
  private updateSubMenuPreferences(subMenu: JobPostSubNavigation, jobKind: string): void {
    if (subMenu.value === 'preferences') {
      subMenu.isShown = jobKind === 'public';
    }
  }

  /**
   * Update sub menus isShown and isCompleted status based on jobObj.
   *
   * @param subMenu - the side navigation sub menu.
   * @param jobObj - the jobObj from draft job.
   * @param isMatchedSubNavigationFound - Check if sub menu value equals to navigation tracker subNavigation value.
   */
  private updateSubMenuByJobObj(
    subMenu: JobPostSubNavigation,
    jobObj: JobDetail,
    isMatchedSubNavigationFound: boolean,
  ): void {
    // Determine which sub menus to show based on jobObj value.
    let newNavigationValueMappingJobObj = {};

    newNavigationValueMappingJobObj = {
      ...navigationValueMappingJobObj,
      alliedHealth: ['basicAllied'],
    };

    const jobObjMappingResult = newNavigationValueMappingJobObj[subMenu.value];
    if (jobObjMappingResult?.length === 1) {
      // If the sub menu service is in the save draft jobObj, then set isShown to true.
      subMenu.isShown =
        jobObj[jobObjMappingResult[0]] || jobObj?.searchFilter?.basicSearch?.includes(jobObjMappingResult[0]);
    } else if (jobObjMappingResult?.length > 1) {
      // Eg. For sub menu service like socialAndCommunitySupportDetails, it is showing when selecting either basic_community or basic_companionship.
      for (const result of jobObjMappingResult) {
        subMenu.isShown = jobObj?.searchFilter?.basicSearch?.includes(result);
        if (subMenu.isShown) {
          break;
        }
      }
    }

    // All sub menus before sub menu saved in navigation tracker are all set to completed if they are shown.
    subMenu.isCompleted = !isMatchedSubNavigationFound && subMenu.isShown;
  }

  /**
   * Create navigation tracker object from the side navigation menu of the draft.
   *
   * @param navigation - The side navigation menu.
   */
  private createNavigationTrackerObject(navigation: JobPostNavigation['navigation']): NavigationTracker {
    let navigationTracker: NavigationTracker;
    for (const menu of navigation) {
      let isSubMenuShownAndIncomplete = false;
      for (const subMenu of menu.subNavigation) {
        // When the submenu is shown and incomplete, save this sub menu value and parent menu value to navigation tracker
        if (!subMenu.isCompleted && subMenu.isShown) {
          isSubMenuShownAndIncomplete = true;
          navigationTracker = {
            navigationValue: menu.value,
            subNavigationValue: subMenu.value,
          };
          break;
        }
      }
      if (isSubMenuShownAndIncomplete) {
        break;
      }
    }
    return navigationTracker;
  }

  /**
   * Retrieves the query params used for tracking job creation.
   *
   * @param postJobObject - The source job used to check if existing tracking already exists.
   * @returns A PostJobQueryParams object containing all the available tracking properties.
   */
  private getTrackingFromQueryParams(postJobObject: JobDetail): PostJobQueryParams {
    // Only apply the query params if the current values are EMPTY
    return {
      source: postJobObject?.source || this.activatedRoute.snapshot.queryParamMap.get(POSTJOB_SOURCE_QUERY_PARAM),
      origin: postJobObject?.origin || this.activatedRoute.snapshot.queryParamMap.get(POSTJOB_ORIGIN_QUERY_PARAM),

      // 1.) scheduledEventDerivedUniqueId and scheduledEventOccurrenceId is to indicate that the job being
      //      created is a replacement for a cancelled session
      // 2.) prioritise identifiers from the query param over the draft job identifiers. this is so that the created
      //      job is linked correctly to the cancelled session
      scheduledEventDerivedUniqueId:
        this.activatedRoute.snapshot.queryParamMap.get(SCHEDULED_EVENT_DERIVED_UNIQUE_ID_PARAM) ||
        postJobObject?.scheduledEventDerivedUniqueId,

      scheduledEventOccurrenceId:
        this.activatedRoute.snapshot.queryParamMap.get(SCHEDULED_EVENT_OCCURRENCE_ID_PARAM) ||
        postJobObject?.scheduledEventOccurrenceId,
    };
  }

  /**
   * Initialise navigation menu which controls the display job form pages.
   *
   * @param jobType - type of job i.e 'public' or 'private'
   */
  initialiseSideNavigation(jobType: string): void {
    this.updateCurrentStepValue(1);
    this.sideNav = {
      title: 'Post a job',
      navigation: postJobsServiceNavigation,
    };

    this.navigationSubMenu = postJobsServiceNavigationSubMenu;

    if (jobType === 'public') {
      this.hideShowSubNavItems(['preferences'], true);
    }

    // Sets total steps in job flow based off navigationSubMenu
    this.countTotalSteps();
  }

  ngOnDestroy() {
    this.postJobsObjectEvent.unsubscribe();
    this.selectedUnsupportedServices.unsubscribe();
    this.onDestroySubject.next();
    this.onDestroySubject.complete();
  }
}
